import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Calendar,
  Star,
  Users,
  Settings,
  LogOut,
  ChevronRight,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const navItems = [
  { title: "Dashboard", href: "/account/dashboard", icon: LayoutDashboard },
  { title: "My Bookings", href: "/account/bookings", icon: Calendar },
  { title: "Points & Rewards", href: "/account/points", icon: Star, showBadge: true },
  { title: "Referrals", href: "/account/membership", icon: Users },
  { title: "Preferences", href: "/account/transactions", icon: Settings },
];

const tierColors: Record<string, string> = {
  bronze: "bg-[hsl(30,50%,50%)]",
  silver: "bg-[hsl(0,0%,70%)]",
  gold: "bg-[hsl(var(--flexaura-accent))]",
};

export function AccountSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [tier, setTier] = useState("bronze");
  const [pointsBalance, setPointsBalance] = useState<number | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    supabase.rpc("get_user_points", { p_user_id: user.id }).then(({ data }) => {
      if (data && data.length > 0) {
        setPointsBalance((data[0] as any).available_points ?? 0);
        setTier((data[0] as any).tier ?? "bronze");
      }
    });

    supabase
      .from("users")
      .select("display_name, full_name")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          const name = data.display_name || data.full_name || "";
          setFirstName(name.split(" ")[0] || "Member");
        }
      });
  }, [user?.id]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <Sidebar className={collapsed ? "w-14" : "w-60"} collapsible="icon">
      <SidebarContent className="flex flex-col h-full">
        {/* User info at top */}
        {!collapsed && (
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm truncate">{firstName}</p>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold capitalize text-[hsl(0,0%,100%)] ${tierColors[tier] ?? ""}`}
              >
                {tier}
              </span>
            </div>
            {pointsBalance !== null && (
              <p className="font-mono text-xs font-semibold text-[hsl(var(--flexaura-accent))] mt-0.5">
                {pointsBalance.toLocaleString()} pts
              </p>
            )}
          </div>
        )}

        <SidebarGroup className="flex-1">
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-accent/20 text-primary font-medium"
                          : "hover:bg-accent/10"
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                      {!collapsed && item.showBadge && pointsBalance !== null && (
                        <span className="ml-auto text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-full bg-[hsl(var(--flexaura-accent)/0.15)] text-[hsl(var(--flexaura-accent))]">
                          {pointsBalance.toLocaleString()}
                        </span>
                      )}
                      {!collapsed && !item.showBadge && (
                        <ChevronRight className="ml-auto h-4 w-4 opacity-50" />
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Sign Out at bottom */}
        <div className="border-t border-border p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={handleSignOut} className="text-muted-foreground hover:text-foreground">
                <LogOut className="h-4 w-4" />
                {!collapsed && <span>Sign Out</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
